import type { HttpError } from "../services/httpService";
import { formatNumberFixed } from "./numberUtils";
import type {
  CreateNotificationDraftRequest,
  NotificationChannel,
  NotificationChannelContentInput,
  NotificationChannelRequirement,
  NotificationRegistrySnapshot,
  UpdateNotificationDraftRequest,
} from "../types/adminNotifications";

/**
 * Pure helpers for the admin notifications UI (PWA-2690): contact-endpoint
 * validation, gateway error mapping, and the draft DTO builders / validators
 * used by the composer.
 */

/** Backend cap on the notification email (`ReplaceEmailRequest.maxLength`). */
export const NOTIFICATION_EMAIL_MAX_LENGTH = 320;
/** Digits in a notification endpoint verification code (PWA-2698). */
export const NOTIFICATION_VERIFICATION_CODE_LENGTH = 6;

/** Audience cap after deduplication (`CreateNotificationDraftRequest.userIds`). */
export const NOTIFICATION_MAX_RECIPIENTS = 1000;

/** Per-ID length cap (`userIds.items.maxLength`). */
export const NOTIFICATION_USER_ID_MAX_LENGTH = 255;

/** Gateway request-body cap, measured in UTF-8 bytes of the JSON body. */
export const NOTIFICATION_MAX_BODY_BYTES = 64 * 1024;

/** E.164 as enforced by the gateway (`ReplaceSmsRequest.pattern`). */
const E164_PATTERN = /^\+[1-9][0-9]{7,14}$/;

/** Basic shape check only; the backend applies the authoritative email rules. */
const EMAIL_SHAPE_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** `ActionUrl` schema: `^https://`. */
const ACTION_URL_PATTERN = /^https:\/\/\S+$/;

export function isValidNotificationEmail(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= NOTIFICATION_EMAIL_MAX_LENGTH && EMAIL_SHAPE_PATTERN.test(trimmed);
}

export function isValidE164(value: string): boolean {
  return E164_PATTERN.test(value.trim());
}

export function isValidActionUrl(value: string, channel?: NotificationChannel): boolean {
  if (!ACTION_URL_PATTERN.test(value.trim())) return false;
  if (channel !== "INTERCOM_CHAT") return true;
  // Chat links are rendered by the provider; match its stricter input contract.
  try {
    const parsedUrl = new URL(value.trim());
    return parsedUrl.protocol === "https:" && !parsedUrl.username && !parsedUrl.password;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Draft DTO builders
// ---------------------------------------------------------------------------

/** Editor fields for one channel. Unused fields are ignored by the builder. */
export interface ChannelContentFields {
  subject?: string;
  title?: string;
  body?: string;
  actionUrl?: string;
}

/** One enabled channel in the composer, in selection order. */
export interface DraftChannelState extends ChannelContentFields {
  channel: NotificationChannel;
}

/** Composer state that the DTO builders and validators read. */
export interface DraftFormState {
  categoryKey: string;
  subcategoryKey: string;
  userIds: string[];
  /** IDs the admin added that the user lookup could not resolve; they block Save. */
  unresolvedUserIds?: string[];
  channels: DraftChannelState[];
}

export const HTML_BODY_CHANNELS: readonly NotificationChannel[] = ["EMAIL", "WEB", "MOBILE_IN_APP"];

export function isHtmlBodyChannel(channel: NotificationChannel): boolean {
  return HTML_BODY_CHANNELS.includes(channel);
}

/**
 * Builds the exact input DTO for a channel. Every input schema is
 * `additionalProperties: false`, so only the fields that channel accepts are
 * emitted and `actionUrl` is omitted when blank. Bodies are passed verbatim.
 */
export function buildChannelContentInput(
  channel: NotificationChannel,
  fields: ChannelContentFields
): NotificationChannelContentInput {
  const body = fields.body ?? "";
  const actionUrl = fields.actionUrl?.trim();
  const optional = actionUrl ? { actionUrl } : {};
  switch (channel) {
    case "EMAIL":
      return { channel, subject: fields.subject ?? "", body, ...optional };
    case "SMS":
      return { channel, body, ...optional };
    default:
      return { channel, title: fields.title ?? "", body, ...optional };
  }
}

/** Dedupes trimmed IDs in first-seen order, dropping empties (the backend does the same). */
export function normalizeUserIds(userIds: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  userIds.forEach(raw => {
    const id = raw.trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    result.push(id);
  });
  return result;
}

export function buildDraftInput(state: DraftFormState): CreateNotificationDraftRequest {
  return {
    categoryKey: state.categoryKey,
    subcategoryKey: state.subcategoryKey,
    userIds: normalizeUserIds(state.userIds),
    channelContents: state.channels.map(entry => buildChannelContentInput(entry.channel, entry)),
  };
}

/** Same measurement as the gateway (`Buffer.byteLength(JSON.stringify(body), "utf8")`). */
export function draftInputBytes(input: CreateNotificationDraftRequest | UpdateNotificationDraftRequest): number {
  return new TextEncoder().encode(JSON.stringify(input)).length;
}

// ---------------------------------------------------------------------------
// Registry helpers
// ---------------------------------------------------------------------------

export function allowedChannels(
  registry: NotificationRegistrySnapshot | null,
  categoryKey: string,
  subcategoryKey: string
): NotificationChannel[] {
  const category = registry?.categories.find(entry => entry.key === categoryKey);
  const subcategory = category?.subcategories.find(entry => entry.key === subcategoryKey);
  return subcategory ? subcategory.channels.map(policy => policy.channel) : [];
}

/** Channels that must be enabled alongside the given selection (e.g. MOBILE_PUSH -> MOBILE_IN_APP). */
export function requiredCompanions(
  channels: readonly NotificationChannel[],
  requirements: readonly NotificationChannelRequirement[]
): NotificationChannel[] {
  const selected = new Set(channels);
  const companions: NotificationChannel[] = [];
  requirements.forEach(requirement => {
    if (!selected.has(requirement.channel)) return;
    requirement.includesChannels.forEach(companion => {
      if (!selected.has(companion) && !companions.includes(companion)) companions.push(companion);
    });
  });
  return companions;
}

/** Channels that require `channel` to stay enabled (the inverse of requiredCompanions). */
export function requiredBy(
  channel: NotificationChannel,
  channels: readonly NotificationChannel[],
  requirements: readonly NotificationChannelRequirement[]
): NotificationChannel[] {
  return requirements
    .filter(requirement => channels.includes(requirement.channel) && requirement.includesChannels.includes(channel))
    .map(requirement => requirement.channel);
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Blocking errors for Save: only what the backend rejects on create/replace.
 * Partial drafts (no recipients, no channels, empty text) are allowed.
 */
export function validateDraftForSave(state: DraftFormState, registry: NotificationRegistrySnapshot | null): string[] {
  const errors: string[] = [];
  const category = registry?.categories.find(entry => entry.key === state.categoryKey);
  if (!state.categoryKey) errors.push("Choose a category.");
  else if (registry && !category) errors.push(`Category ${state.categoryKey} is not available.`);
  const subcategory = category?.subcategories.find(entry => entry.key === state.subcategoryKey);
  if (!state.subcategoryKey) errors.push("Choose a subcategory.");
  else if (category && !subcategory) errors.push(`Subcategory ${state.subcategoryKey} is not available under ${state.categoryKey}.`);

  const allowed = subcategory ? subcategory.channels.map(policy => policy.channel) : null;
  const seenChannels = new Set<NotificationChannel>();
  state.channels.forEach(entry => {
    if (seenChannels.has(entry.channel)) errors.push(`Channel ${entry.channel} is listed twice.`);
    seenChannels.add(entry.channel);
    if (allowed && !allowed.includes(entry.channel)) {
      errors.push(`Channel ${entry.channel} is not available for ${state.categoryKey} / ${state.subcategoryKey}.`);
    }
    const actionUrl = entry.actionUrl?.trim();
    if (actionUrl && !isValidActionUrl(actionUrl, entry.channel)) {
      errors.push(entry.channel === "INTERCOM_CHAT"
        ? "INTERCOM_CHAT: action URL must be a valid HTTPS URL without embedded credentials."
        : `${entry.channel}: action URL must start with https://.`);
    }
  });

  const ids = normalizeUserIds(state.userIds);
  if (ids.length > NOTIFICATION_MAX_RECIPIENTS) {
    errors.push(`Audience has ${ids.length.toLocaleString("en-US")} recipients; the limit is ${NOTIFICATION_MAX_RECIPIENTS.toLocaleString("en-US")}.`);
  }
  if (ids.some(id => id.length > NOTIFICATION_USER_ID_MAX_LENGTH)) {
    errors.push(`Every user ID must be at most ${NOTIFICATION_USER_ID_MAX_LENGTH} characters.`);
  }
  const unresolved = (state.unresolvedUserIds ?? []).filter(id => ids.includes(id));
  if (unresolved.length > 0) {
    errors.push(`${unresolved.length} recipient ID${unresolved.length === 1 ? " was" : "s were"} not found. Remove them before saving.`);
  }

  const bytes = draftInputBytes(buildDraftInput(state));
  if (bytes > NOTIFICATION_MAX_BODY_BYTES) {
    errors.push(`Content is ${formatKilobytes(bytes)} KB; the limit is 64 KB.`);
  }
  return errors;
}

/** Save errors plus finalize prerequisites, including the chat provider's title requirement. */
export function validateDraftForFinalize(state: DraftFormState, registry: NotificationRegistrySnapshot | null): string[] {
  const errors = validateDraftForSave(state, registry);
  if (normalizeUserIds(state.userIds).length === 0) errors.push("Add at least one recipient.");
  if (state.channels.length === 0) errors.push("Enable at least one channel.");
  if (state.channels.some(entry => entry.channel === "INTERCOM_CHAT" && !entry.title?.trim())) {
    errors.push("Intercom chat needs a title before sending or scheduling.");
  }
  const channels = state.channels.map(entry => entry.channel);
  const missing = requiredCompanions(channels, registry?.channelRequirements ?? []);
  missing.forEach(companion => {
    const owner = requiredBy(companion, channels, registry?.channelRequirements ?? []).join(", ");
    errors.push(`${owner} requires ${companion} content.`);
  });
  return errors;
}

export function formatKilobytes(bytes: number): string {
  return formatNumberFixed(bytes / 1024, 1);
}

// ---------------------------------------------------------------------------
// UTC schedule helpers
// ---------------------------------------------------------------------------

const YMD_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const HM_PATTERN = /^(\d{2}):(\d{2})$/;

/** Combines a `YYYY-MM-DD` date and `HH:mm` time, both UTC, into `...T HH:mm:00.000Z`; null when invalid. */
export function toScheduledForUtc(dateYmd: string, timeHm: string): string | null {
  const date = YMD_PATTERN.exec(dateYmd.trim());
  const time = HM_PATTERN.exec(timeHm.trim());
  if (!date || !time) return null;
  const [, year, month, day] = date.map(Number);
  const [, hour, minute] = time.map(Number);
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return null;
  const millis = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  const candidate = new Date(millis);
  // Reject calendar overflow such as Feb 30 rather than silently rolling into March.
  if (candidate.getUTCMonth() !== month - 1 || candidate.getUTCDate() !== day) return null;
  return candidate.toISOString();
}

export function isFutureUtc(iso: string, now: number = Date.now()): boolean {
  const millis = Date.parse(iso);
  return Number.isFinite(millis) && millis > now;
}

/**
 * UI-only lead time for Schedule and Reschedule (PWA-2748 Section 3.11). The
 * backend only requires a future time; this guards against moving something
 * that is about to fire.
 */
export const NOTIFICATION_SCHEDULE_MIN_LEAD_MINUTES = 10;

export function isAtLeastMinutesAhead(iso: string, minutes: number, now: number = Date.now()): boolean {
  const millis = Date.parse(iso);
  return Number.isFinite(millis) && millis - now >= minutes * 60_000;
}

// ---------------------------------------------------------------------------
// SMS sizing
// ---------------------------------------------------------------------------

/** GSM-7 segment count (160 single, 153 per concatenated part). Non-GSM text is not detected. */
export const SMS_SINGLE_SEGMENT_LENGTH = 160;
export const SMS_MULTI_SEGMENT_LENGTH = 153;

export function smsSegmentCount(text: string): number {
  const length = text.length;
  if (length === 0) return 0;
  if (length <= SMS_SINGLE_SEGMENT_LENGTH) return 1;
  return Math.ceil(length / SMS_MULTI_SEGMENT_LENGTH);
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export type NotificationErrorKind =
  | "validation"
  | "auth"
  | "forbidden"
  | "not-found"
  | "conflict"
  | "too-large"
  | "rate-limited"
  | "unavailable"
  | "ambiguous"
  | "unknown";

export interface NotificationErrorDescription {
  status?: number;
  code?: string;
  message: string;
  kind: NotificationErrorKind;
  /** True when the write may already have committed and must not be retried blindly. */
  ambiguous: boolean;
}

const DEFAULT_MESSAGES: Record<number, string> = {
  400: "The request was rejected as invalid.",
  401: "Your session has expired. Sign in again.",
  403: "Admin access is required.",
  404: "Not found.",
  409: "This item changed since it was loaded. Refresh and try again.",
  413: "The content exceeds the 64 KB limit.",
  429: "Too many requests. Wait a moment and try again.",
  500: "The notification request failed.",
  502: "The notification service could not be reached.",
  503: "Notifications are unavailable right now.",
  504: "The notification service timed out.",
};

function kindForStatus(status: number | undefined): NotificationErrorKind {
  if (status === undefined) return "ambiguous";
  switch (status) {
    case 400: return "validation";
    case 401: return "auth";
    case 403: return "forbidden";
    case 404: return "not-found";
    case 409: return "conflict";
    case 413: return "too-large";
    case 429: return "rate-limited";
    case 503: return "unavailable";
    case 408:
    case 502:
    case 504:
      return "ambiguous";
    default:
      return "unknown";
  }
}

/**
 * Maps an httpService error to a safe, user-facing description.
 *
 * Message precedence: gateway domain body `{ error: { code, message } }`,
 * then auth middleware `{ error: string }`, then a per-status default. A
 * client timeout (httpService reports status 408), a network failure (no
 * status), a 502 or a 504 are all ambiguous for writes: the request may have
 * committed, so callers refetch instead of retrying.
 */
export function describeNotificationError(error: unknown): NotificationErrorDescription {
  const httpError = (error ?? {}) as HttpError;
  const status = typeof httpError.status === "number" ? httpError.status : httpError.response?.status;
  const body = httpError.response?.data as { error?: unknown } | undefined;
  let code: string | undefined;
  let message: string | undefined;
  if (body && typeof body === "object" && body.error !== undefined) {
    if (typeof body.error === "string") {
      message = body.error;
    } else if (body.error && typeof body.error === "object") {
      const domain = body.error as { code?: unknown; message?: unknown };
      if (typeof domain.code === "string") code = domain.code;
      if (typeof domain.message === "string") message = domain.message;
    }
  }
  const kind = kindForStatus(status);
  if (!message) {
    message = status !== undefined && DEFAULT_MESSAGES[status]
      ? DEFAULT_MESSAGES[status]
      : kind === "ambiguous"
        ? "The request could not be confirmed. Refresh to check its result."
        : "The notification request failed.";
  }
  return { status, code, message, kind, ambiguous: kind === "ambiguous" };
}
